import { SingleUser, UserRelationRoleEnum, FileResponse, DossierResponse } from '../api/types';
import { User } from './User';
import { store } from '../app/store';

export interface DossierInterface {
  id: number
  createdAt: string|Date;
  lastActivityAt: string|Date;
  aboutUser: SingleUser;
  userRole: UserRelationRoleEnum;
  archived: boolean;
}

export interface GetRollTextProps {
  capitalize?: boolean;
  pluralize?: boolean;
}

export class Dossier implements DossierInterface {
  private static readonly userRelationRoleToTextMap = {
    [UserRelationRoleEnum.client]: 'cliënt',
    [UserRelationRoleEnum.caregiver]: 'behandelaar',
    [UserRelationRoleEnum.responsibleParty]: 'ouder',
    [UserRelationRoleEnum.thirdParty]: 'betrokkene',
    [UserRelationRoleEnum.administrator]: 'administrator',
  };

  private static readonly userRelationRoleToTextPluralsMap = {
    [UserRelationRoleEnum.client]: 'cliënten',
    [UserRelationRoleEnum.caregiver]: 'behandelaren',
    [UserRelationRoleEnum.responsibleParty]: 'ouders',
    [UserRelationRoleEnum.thirdParty]: 'betrokkenen',
    [UserRelationRoleEnum.administrator]: 'administratoren',
  };

  id: number;
  createdAt: Date;
  lastActivityAt: Date;
  aboutUser: User;
  userRole: UserRelationRoleEnum;
  archived: boolean;

  constructor({ id, createdAt, lastActivityAt, aboutUser, userRole, archived }: DossierInterface) {
    this.id = id;
    this.createdAt = new Date(createdAt);
    this.lastActivityAt = new Date(lastActivityAt);
    this.aboutUser = new User(aboutUser);
    this.userRole = userRole;
    this.archived = archived;
  }

  static fromResponse(dossiersResponse: DossierResponse[], userRole?: UserRelationRoleEnum): Dossier[] {
    return dossiersResponse.map(dossierResponse => {
      const { id, createdAt, lastActivityAt, relations, archived } = dossierResponse;
      let role = userRole;
      if (!role) {
        const currentUserId = store.getState().session.user?.id;
        const currentUserRole = relations.find(relation => relation.user.id === currentUserId);
        if (!currentUserId || !currentUserRole) {
          throw new Error('Missing current user in state');
        }
        role = currentUserRole.relationRole;
      }
      return new Dossier({
        id,
        createdAt,
        lastActivityAt,
        aboutUser: dossierResponse.aboutUser,
        userRole: role,
        archived,
      });
    });
  }

  private static getAboutUser(fileResponse: FileResponse): SingleUser {
    const aboutUser = fileResponse.users.find(user => user.relationRole === UserRelationRoleEnum.client);
    if (!aboutUser) {
      throw new Error('File is missing a client');
    }
    return aboutUser.userId;
  }

  static getRoleText(role: UserRelationRoleEnum, { capitalize, pluralize }: GetRollTextProps = {}): string {
    const map = pluralize ? Dossier.userRelationRoleToTextPluralsMap : Dossier.userRelationRoleToTextMap;
    const text = map[role] || '[onbekend rol type]';
    if (capitalize) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    } else {
      return text;
    }
  }

  getRoleText(options?: GetRollTextProps): string {
    return Dossier.getRoleText(this.userRole, options);
  }

  serialize(): DossierInterface {
    return { ...this, aboutUser: this.aboutUser.toSingleUser() };
  }

  match(dossier: DossierInterface): boolean {
    return dossier.id === this.id;
  }

  getFormattedCreatedAt(): string {
    return `${this.dateToDay(this.createdAt)} ${this.dateToTime(this.createdAt)}`;
  }

  getFormattedLastActivityAt(): string {
    return `${this.dateToDay(this.lastActivityAt)} ${this.dateToTime(this.lastActivityAt)}`;
  }

  private dateToTime(date: Date): string {
    return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  }

  private dateToDay(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('nl-NL', options);
  }
}
